<template>
    <DsTooltip
        :closeDelay="100"
        :hideTooltipOnClick
        :openDelay="trigger === 'click' ? 0 : 500"
        :tooltipLocation
        :tooltipText
        :trigger
    >
        <template
            v-if="$slots.tooltipContent"
            #content
        >
            <slot name="tooltipContent" />
        </template>
        <DsButton
            :aria-label="$attrs['aria-label'] || tooltipText"
            :disabled
            iconOnly
            :onDarkBg
            :size
            variant="icon"
            v-bind="$attrs"
        >
            <DsIcon
                class="align-middle"
                :icon
                :size="iconSize"
                :spin
            />
        </DsButton>
    </DsTooltip>
</template>

<script setup lang="ts">
import type { DsTooltipProps, DsTooltipTrigger } from '@/components/design-system/tooltip/tooltip.types';
import type { DsButtonProps } from '@ds/button/DsButton.vue';
import DsButton from '@ds/button/DsButton.vue';
import DsIcon, { type DsIconProps } from '@ds/DsIcon.vue';
import DsTooltip from '@ds/tooltip/DsTooltip.vue';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';
import type { TooltipPlacementType } from '../design-system';

export type IconButtonSize = 'md' | 'sm' | 'xs';

export interface DsIconButtonProps {
    icon: FontAwesomeIconProps['icon'];
    onDarkBg?: DsButtonProps['onDarkBg'];
    size: IconButtonSize;
    spin?: DsIconProps['spin'];
    tooltipLocation?: TooltipPlacementType;
    tooltipText?: DsTooltipProps['tooltipText'];
    hideTooltipOnClick?: DsTooltipProps['hideTooltipOnClick'];
    trigger?: DsTooltipTrigger;
    disabled?: DsButtonProps['disabled'];
}

defineSlots<{
    tooltipContent?: () => void;
}>();

const props = withDefaults(defineProps<DsIconButtonProps>(), {
    onDarkBg: false,
    tooltipLocation: 'top',
    spin: false,
    hideTooltipOnClick: true,
    trigger: 'mouseenter focusin',
});

const ICON_SIZE_BY_BUTTON_SIZE = {
    xs: 'sm',
    sm: 'md',
    md: 'lg',
} as const satisfies Record<IconButtonSize, DsIconProps['size']>;

const iconSize = computed(() => ICON_SIZE_BY_BUTTON_SIZE[props.size]);
</script>
